import { untrack } from "solid-js";
import { createStore } from "solid-js/store";

import type { Channel, Client, Server } from "stoat.js";

type Entry = { base: string; count: number; exact: boolean };

const [counts, setCounts] = createStore<Record<string, Entry>>({});
const loading = new Set<string>();
const hooked = new WeakSet<Client>();
let queue: Promise<void> = Promise.resolve();

/** Ultima mensagem lida (vazio se nunca leu) */
function readBase(client: Client, channelId: string): string {
  return client.channelUnreads.get(channelId)?.lastMessageId ?? "";
}

/**
 * Mensagem nova de outra pessoa soma 1 (enquanto o marcador de leitura for o mesmo)
 */
export function trackUnreadCounts(client: Client) {
  if (hooked.has(client)) return;
  hooked.add(client);

  client.addListener("messageCreate", (message) => {
    if (message.authorId === client.user?.id) return;
    const channel = client.channels.get(message.channelId);
    if (!channel || channel.type === "SavedMessages") return;

    const base = readBase(client, channel.id);
    const old = counts[channel.id];
    if (old && old.base === base) {
      setCounts(channel.id, { ...old, count: old.count + 1 });
    } else {
      setCounts(channel.id, { base, count: 1, exact: false });
    }
  });
}

async function load(
  client: Client,
  channel: Channel,
  base: string,
  key: string,
): Promise<void> {
  try {
    const self = client.user?.id;
    const seen = new Set<string>();
    let cursor = base;
    let total = 0;
    let ok = false;

    for (let page = 0; page < 10; page++) {
      let msgs;
      try {
        msgs = await channel.fetchMessages({
          limit: 100,
          after: cursor,
          sort: "Oldest",
        });
      } catch {
        break;
      }
      ok = true;

      let fresh = 0;
      let newest = cursor;
      for (const m of msgs) {
        if (seen.has(m.id)) continue;
        seen.add(m.id);
        fresh++;
        if (m.authorId !== self) total++;
        if (m.id > newest) newest = m.id;
      }
      if (fresh === 0) break;
      cursor = newest;
      if (msgs.length < 100) break;
    }

    if (ok && readBase(client, channel.id) === base) {
      setCounts(channel.id, { base, count: total, exact: true });
    }
    await new Promise((resolve) => setTimeout(resolve, 150));
  } catch {
    // ignora
  } finally {
    loading.delete(key);
  }
}

/**
 * Busca a contagem real de um canal nao lido (fila, uma por vez)
 */
export function requestUnreadCount(client: Client, channel: Channel) {
  if (!channel.unread || channel.muted) return;
  if ((channel.type as string) === "VoiceChannel") return;

  const base = readBase(client, channel.id);
  if (!base) return;

  const entry = untrack(() => counts[channel.id]);
  if (entry && entry.base === base && entry.exact) return;

  const key = channel.id + ":" + base;
  if (loading.has(key)) return;
  loading.add(key);
  queue = queue.then(() => load(client, channel, base, key));
}

/** Quantidade de mensagens nao lidas de um canal */
export function channelUnreadCount(client: Client, channel: Channel): number {
  if (channel.muted || !channel.unread) return 0;
  const entry = counts[channel.id];
  if (!entry || entry.base !== readBase(client, channel.id)) return 0;
  return entry.count;
}

/** Soma dos canais do servidor */
export function serverUnreadCount(client: Client, server: Server): number {
  return server.channels.reduce(
    (sum, channel) => sum + channelUnreadCount(client, channel),
    0,
  );
}
